import React, { useCallback, useState, useLayoutEffect, useRef, useEffect } from 'react';
import { useMappedState, useDispatch } from 'redux-react-hook';
import { useTransition } from 'react-spring';
import _isEmpty from 'lodash/isEmpty';
import { getNextQuestion, getPreviousQuestion, goBack } from 'redux/actions/consultation';
import { getLastQuestion } from 'redux/actions/consultation/question';
import { confirmCancelProgress } from 'redux/actions/session';
import { consultationSelector } from 'redux/selectors/consultation';
import Card from 'styles/blocks/card';
import { usePrevious, useMeasure, usePrefersReducedMotion } from 'common/customHooks';
import { renderQuestionComponent } from 'components/consultation/question';
import { loadLocalStorage, saveLocalStorage } from 'redux/sessionStorage';
import { STRINGS } from 'config';
import { ValidationMessages } from 'styles/blocks/validationMessages';
import { BackwardsNavigation } from './backwardsNavigation';

const { PRODUCT: { PRIMARY_CARE, E_SPECIALIST } } = STRINGS;

export function QuestionCard({ isClinicalReviewMode, header, product }) {
  const mapState = useCallback(state => consultationSelector(state), []);
  const {
    question,
    validationErrors,
    isButtonsDisabled,
    isForwardNavigation,
    isCompoundQuestion,
    isDisclaimerQuestion,
    publicHealthAlertHtml,
    federatedLoginProvider,
    isLoggedIn,
    isSessionActive,
    validationMessages,
    previousAnswers,
  } = useMappedState(mapState);

  const containerRef = useRef(null);

  const [isAnimationDisabled, setIsAnimationDisabled] = useState(false);

  useEffect(() => {
    const localStorage = loadLocalStorage();
    setIsAnimationDisabled(localStorage.isAnimationDisabled || false);
  }, []);

  const toggleAnimation = () => {
    setIsAnimationDisabled(!isAnimationDisabled);
    saveLocalStorage({ isAnimationDisabled: !isAnimationDisabled });
  };

  /*
    The following methods control the animation of the questions.
    - [animatedQuestions] -- is an object with two keys: '0' and '1'.
        The initial state of this component holds an empty question object in the 0 position.
        As the next question is returned by the API it is added to position 1.
        This was needed in order to animate OUT the previous question while animating IN the next.
        This is because at certain times during the animation 2 question components are present in the DOM.
        The unusual use of renderQuestionComponent() function instead of a component is to prevent the error:
        'Cannot perform update on an unmounted component' and prime the component with the NEXT question before render.
    - [position] -- is at any time either the number '0' or '1'
        and controls at which position the incoming question is held at in the [animatedQuestions] object.
    - [animatedQuestionHeight] -- is the height of the newly loaded question component.
        We need to know this to know from which position to animate the question.
    - [isMounted] -- is used to determine whether or not the first answer is focusable.
        You cannot focus an element which is not yet mounted to the DOM. A method is listening to the animation onRest.
    - [bind, { height }] = useMeasure() -- uses the custom hook, 'useMeasure'.
        This is used to measure the height of the react ui within the window to inform from which position to animate the question.
    - viewerHeight -- compares the react ui height and question component height, an animates the question using the larger of the two measures.
  */

  const [animatedQuestions, setAnimatedQuestions] = useState({ 0: renderQuestionComponent({ answers: [], questionId: '', text: '' }), 1: null });

  const [position, setPosition] = useState(0);

  const [animatedQuestionHeight, setHeight] = useState(0);

  const [isMounted, setIsMounted] = useState(false);

  const [bind, { height }] = useMeasure();

  const viewerHeight = animatedQuestionHeight > height ? animatedQuestionHeight : height;

  const padding = animatedQuestionHeight === 0 ? 0 : 50;

  // Create actions
  const dispatch = useDispatch();

  const [primaryCareOrESpecialist] = useState(() => product === PRIMARY_CARE || product === E_SPECIALIST);

  useEffect(() => {
    if (_isEmpty(question) && isSessionActive && primaryCareOrESpecialist) {
      dispatch(getLastQuestion());
    }
  }, [dispatch, isSessionActive, question, product, primaryCareOrESpecialist]);

  const nextQuestion = useCallback(() => {
    return dispatch(getNextQuestion());
  }, [dispatch]);

  const memoizedNextQuestion = useCallback(() => {
    if (primaryCareOrESpecialist) {
      return isButtonsDisabled || !isMounted ? null : nextQuestion();
    }
    return isButtonsDisabled ? null : nextQuestion();
  }, [nextQuestion, isButtonsDisabled, isMounted, primaryCareOrESpecialist]);

  const prevQuestion = useCallback(() => {
    if (isButtonsDisabled) {
      return null;
    }
    if (primaryCareOrESpecialist) {
      return dispatch(goBack());
    }
    return dispatch(getPreviousQuestion());
  }, [dispatch, isButtonsDisabled, primaryCareOrESpecialist]);

  const handleSetIsMounted = () => {
    return setIsMounted(true);
  };

  const prefersReducedMotion = usePrefersReducedMotion();

  const transitions = useTransition(position, p => p, {
    unique: true,
    reset: true,
    ...(primaryCareOrESpecialist ? ({
      from: { transform: `translate3d(0,${isForwardNavigation ? '' : '-'}${viewerHeight + padding}px,0)` },
      enter: { transform: 'translate3d(0,0px,0)' },
      leave: { transform: `translate3d(0,${isForwardNavigation ? '-' : ''}${viewerHeight + padding}px,0)` },
      config: { tension: 340, friction: 40 },
    }) : ({
      from: { transform: 'translate3d(0,-22px,0)', visibility: 'visible' },
      enter: { transform: 'translate3d(0,0px,0)', visibility: 'visible' },
      leave: { transform: 'translate3d(0,0px,0)', visibility: 'hidden' },
      config: { tension: 280, friction: 70 },
    })),
    immediate: () => prefersReducedMotion || isAnimationDisabled,
    onRest: handleSetIsMounted,
  });

  const isDifferentQuestion = question.questionId !== usePrevious(question.questionId);

  /*
    useLayoutEffect is a react hook. It is used when the 'effect' is used for repaint.
    https://reactjs.org/docs/hooks-reference.html#uselayouteffect
    This hook listens for a new question in state and verifies that it is different to the last.
    It then adds the question to the unused [animatedQuestions] position.
    It then points the [position] at that new question.
  */

  useLayoutEffect(() => {
    if (isDifferentQuestion) {
      setIsMounted(false);
      const nextPosition = (position + 1) % 2;
      setAnimatedQuestions({ ...animatedQuestions, [nextPosition]: renderQuestionComponent(question) });
      setPosition(nextPosition);
      // Scroll container div back to top so new question is shown in full
      if (containerRef !== null) {
        containerRef.current.scrollTop = 0;
      }
    }
  }, [question, animatedQuestions, position, isDifferentQuestion]);

  /*
    The renderQuestion function below returns an updating Question component if IE9 else returns animated components.
  */

  const renderQuestion = () => {
    return transitions.map(({ item, props, key }) => {
      const Question = animatedQuestions[item];
      return (
        <Question
          style={props}
          isMounted={isMounted}
          key={key}
          setParentHeight={setHeight}
          validationErrors={validationErrors}
          isClinicalReviewMode={isClinicalReviewMode}
          onClickNextQuestion={memoizedNextQuestion}
          isCompoundQuestion={isCompoundQuestion}
          containerWidth={containerRef.current && containerRef.current.getBoundingClientRect().width}
          publicHealthAlertHtml={publicHealthAlertHtml}
          isDisclaimerQuestion={isDisclaimerQuestion}
          containerRef={containerRef}
          isAnimationDisabled={isAnimationDisabled}
          onToggleAnimation={toggleAnimation}
          prefersReducedMotion={prefersReducedMotion}
          federatedLoginProvider={federatedLoginProvider}
          isLoggedIn={isLoggedIn}
          product={product}
          previousAnswers={previousAnswers}
        />
      );
    });
  };

  return (
    <Card>
      <ValidationMessages product={product} validationMessages={validationMessages} />
      <BackwardsNavigation onPrevious={prevQuestion} onStartOver={() => dispatch(confirmCancelProgress())} product={product}>
        {isClinicalReviewMode && (
          <Card.Header>QPP - {header}</Card.Header>
        )}
        <Card.Inner ref={bind.ref}>
          <Card.FlexRow>
            <Card.CardContents isMounted={isMounted} ref={containerRef}>
              {renderQuestion()}
            </Card.CardContents>
          </Card.FlexRow>
        </Card.Inner>
      </BackwardsNavigation>
    </Card>
  );
}
